import { useState } from 'react';
import { getErrorMessage } from '../api/client';
import { setCountThreshold } from '../api/inventoryCounts';
import type { CountThreshold, CountThresholdMode } from '../types/inventoryCount';
import { parseDecimalInput } from '../utils/wasteCapture';
import { thresholdUncostedToSend, wasteThresholdBlocker } from '../utils/wasteReview';
import { Answers, ChoiceCards, FooterReason, Question } from './capture';
import { Modal } from './Modal';

/**
 * F3 · Solo el dueño: desde cuánto una diferencia de CONTEO es «grande». Mismas reglas de captura que el
 * umbral de merma (se valida solo lo visible, sin separador de miles, máximo 2 decimales), pero es otro
 * umbral: arriba de él se pide un motivo y una segunda confirmación. Nunca se bloquea el conteo.
 */
export function CountThresholdModal({ current, onClose, onSaved }: { current: CountThreshold; onClose: () => void; onSaved: (next: CountThreshold) => void }) {
  const [mode, setMode] = useState<CountThresholdMode>(current.mode);
  const [value, setValue] = useState(String(current.value));
  const [uncosted, setUncosted] = useState(String(current.uncostedPercentage));
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string>();
  const blocker = wasteThresholdBlocker(mode, value, uncosted);

  const submit = async () => {
    const numeric = parseDecimalInput(value, 2);
    if (isSaving || blocker || numeric === null) return;
    setIsSaving(true);
    setError(undefined);
    try {
      onSaved(await setCountThreshold({ mode, value: numeric, uncostedPercentage: thresholdUncostedToSend(mode, uncosted, current.uncostedPercentage) }));
    } catch (saveError) {
      setError(getErrorMessage(saveError, 'No fue posible guardar el umbral.'));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      title="¿Cuándo una diferencia de conteo es grande?"
      description="Arriba de este número se pide un motivo al contar. El conteo nunca se bloquea."
      isBusy={isSaving}
      onClose={onClose}
      footer={<>
        <FooterReason>{blocker}</FooterReason>
        <button className="text-button" disabled={isSaving} onClick={onClose} type="button">Cancelar</button>
        <button className="button button--primary" disabled={isSaving || Boolean(blocker)} onClick={() => void submit()} type="button">{isSaving ? 'Guardando…' : 'Guardar'}</button>
      </>}
    >
      {error && <div className="alert alert--error" role="alert">{error}</div>}
      <Question>¿Cómo quieres medirla?</Question>
      <ChoiceCards ariaLabel="Cómo medir" onChange={setMode} options={[{ value: 'AMOUNT', label: 'En pesos', hint: 'Ej. desde $300' }, { value: 'PERCENTAGE', label: 'En porcentaje', hint: 'De lo que decía el sistema' }]} value={mode} />
      <Answers>
        <label className="field"><span>{mode === 'AMOUNT' ? 'Desde cuántos pesos' : 'Desde qué porcentaje de lo que decía el sistema'}</span><input inputMode="decimal" onChange={(event) => setValue(event.target.value)} type="text" value={value} /></label>
        {mode === 'AMOUNT' && <label className="field"><span>Si el insumo no tiene costo registrado, desde qué porcentaje de lo que decía el sistema</span><input inputMode="decimal" onChange={(event) => setUncosted(event.target.value)} type="text" value={uncosted} /></label>}
      </Answers>
      <p className="wiz-hint">Es distinto del umbral de merma grande: aquel solo marca la merma para revisión.</p>
    </Modal>
  );
}
